import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { combineLatest, fromEvent, of, ReplaySubject } from 'rxjs';
import { delay, filter, takeWhile, tap, throttleTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as fromDataServices from '../../data-services';
import * as fromShared from '../../shared';
import { DisplayMessageMinimum, ResponseStatus } from '../../shared';
import { SelectItem } from '../../shared/efaact-primeng.module';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';

@Component({
    selector: 'ef-payrate-override',
    templateUrl: './payrate-override.component.html',
    styleUrls: ['./payrate-override.component.scss'],
    standalone: false
})
export class PayrateOverrideComponent
    extends fromShared.Errors
        .DisplayErrors<fromDataServices.PayrateOverride.PayrateOverride>
    implements OnInit, OnDestroy, AfterViewInit
{
    @Input() directReportsSelectItems: SelectItem[];
    @Input() activeDirectReportsSelectItems: SelectItem[];
    @Input() payrollItemsSelectItems: SelectItem[];
    @Input() filteredPayrollItemsSelectItems: SelectItem[];
    @Input() payrollItemsFilterSelectItems: SelectItem[];
    @Input() usersFilterSelectItems: SelectItem[];
    @Input() startDatesSelectItems: SelectItem[];
    @Input() endDatesSelectItems: SelectItem[];
    @Input() payperiodSelectItems: SelectItem[];
    @Input() payrollItemTypesFilterSelectItems: SelectItem[];
    private StartDateSubject = new ReplaySubject<Date>(1);
    private _startDate: Date;
    @Input() public set StartDate(value: Date) {
        this._startDate = value;
        this.StartDateSubject.next(value);
    }
    public get StartDate(): Date {
        return this._startDate;
    }
    private EndDateSubject = new ReplaySubject<Date>(1);
    private _endDate: Date;
    @Input() public set EndDate(value: Date) {
        this._endDate = value;
        this.EndDateSubject.next(value);
    }
    public get EndDate(): Date {
        return this._endDate;
    }
    @Output() Filter = new ReplaySubject<{
        StartDate: string;
        EndDate: string;
    }>(1);
    @Output() Save = new ReplaySubject<
        Partial<fromDataServices.PayrateOverride.PayrateOverride>
    >(1);
    @Output() RecalcRates = new ReplaySubject<{
        StartDate: string;
        EndDate: string;
    }>(1);
    @Output() Delete = new ReplaySubject<
        fromDataServices.PayrateOverride.PayrateOverride[]
    >(1);
    @Output() Select =
        new ReplaySubject<fromDataServices.PayrateOverride.PayrateOverride>(1);
    @Output() SelectDirectReport = new ReplaySubject<string>(1);
    @Output() ClearStatus = new ReplaySubject(1);
    @ViewChild('deleteItems', { read: ElementRef, static: true })
    deleteButton: ElementRef;
    @ViewChild('copyItems', { read: ElementRef, static: true })
    copyButton: ElementRef;
    @ViewChild('updateItems', { read: ElementRef, static: true })
    updateButton: ElementRef;
    @ViewChild('syncPayperiod', { read: ElementRef, static: true })
    syncPayperiodButton: ElementRef;
    @ViewChild('clearFilters', { read: ElementRef, static: true })
    clearButton: ElementRef;
    public editing?: fromDataServices.PayrateOverride.PayrateOverride =
        undefined;
    selected: fromDataServices.PayrateOverride.PayrateOverride[] = [];
    cloned: { [i: number]: fromDataServices.PayrateOverride.PayrateOverride } =
        {};
    filterFormGroup: UntypedFormGroup;
    syncFormGroup: UntypedFormGroup;
    updateFormGroup: UntypedFormGroup;
    moment: any;
    dropdownItemHeight = environment.dropdownItemHeight;
    formAddErrors: {
        severity: string;
        detail: string;
    }[];
    active = true;
    constructor(
        private log: LoggingService,
        public confirmservice: UnsavedChangesService
    ) {
        super();
        this.moment = moment;
        this.filterFormGroup = new UntypedFormGroup({
            StartDate: new UntypedFormControl(null, Validators.required),
            EndDate: new UntypedFormControl(null, Validators.required)
        });
        this.syncFormGroup = new UntypedFormGroup({
            EmployeeListID: new UntypedFormControl(null, Validators.required),
            FromDates: new UntypedFormControl(null, Validators.required),
            StartDate: new UntypedFormControl(null, Validators.required),
            EndDate: new UntypedFormControl(null, Validators.required)
            // PIListID: new FormControl(null, Validators.required)
        });
        this.updateFormGroup = new UntypedFormGroup({
            PayRate: new UntypedFormControl(null, Validators.required)
        });
    }
    ngOnInit(): void {}
    ngOnDestroy(): void {
        this.active = false;
    }
    ngAfterViewInit(): void {
        this.setupSubscriptions();
        setTimeout(() => {
            this.handleOnOpen([0]);
        }, 0);
    }
    setupSubscriptions() {
        combineLatest([this.StartDateSubject, this.EndDateSubject])
            .pipe(
                takeWhile(() => this.active),
                tap(x =>
                    this.log.verbose({
                        type: 'PayrateOverrideComponent:StartDateSubject,EndDateSubject',
                        x
                    })
                ),
                filter(x => !!x[0] && !!x[1])
            )
            .subscribe(([startDate, endDate]) => {
                this.filterFormGroup.controls['StartDate'].setValue(startDate);
                this.filterFormGroup.controls['EndDate'].setValue(endDate);
                this.Filter.next({
                    StartDate: moment(startDate).format('MM/DD/YYYY'),
                    EndDate: moment(endDate).format('MM/DD/YYYY')
                });
            });
        fromEvent(this.syncPayperiodButton.nativeElement, 'click')
            .pipe(
                takeWhile(x => this.active),
                throttleTime(500)
            )
            .subscribe(() => {
                this.clearItemsStatus();
                const values = {
                    ...this.syncFormGroup.value
                    // ...this.filterFormGroup.value
                };
                this.log.verbose({
                    type: 'PayrateOverrideComponent:syncPayperiodButton',
                    values
                });
                this.confirmservice
                    .confirmPromise({
                        headerText: 'Are you sure?',
                        dialogText: `Pay history for ${moment(
                            values.StartDate
                        ).format('L')} through ${moment(values.EndDate).format(
                            'L'
                        )} will be updated. <br />Periods previously processed for this timeframe will be required to be reprocessed. <br />If necessary, adjust the pay period date range to be updated by changing the filter dates.`
                    })
                    .then(() => {
                        const req = {
                            id: -1,
                            Ovrid: -1,
                            UserDirectReports: [
                                ...values.EmployeeListID.map(x => ({
                                    EmployeeListID: x,
                                    EmployeeName: '',
                                    IsActive: true
                                }))
                            ],
                            PIListID: values.PIListID,
                            StartDate: values.StartDate,
                            EndDate: values.EndDate,
                            FromStartDate:
                                values.FromDates &&
                                moment(values.FromDates.split('-')[0]).toDate(),
                            FromEndDate:
                                values.FromDates &&
                                moment(values.FromDates.split('-')[1]).toDate()
                        };
                        this.log.verbose({
                            type: 'PayrateOverrideComponent:syncPayperiodButton:Save',
                            req
                        });
                        this.Save.next(req);
                        this.selected = [];
                    })
                    .catch(e => {
                        this.log.error(e);
                    });
            });
        fromEvent(this.updateButton.nativeElement, 'click')
            .pipe(
                takeWhile(() => this.active),
                throttleTime(500)
            )
            .subscribe(() => {
                const values = this.updateFormGroup.value;
                this.log.verbose({
                    type: 'PayrateOverrideComponent:updateButton',
                    values
                });
                this.items = this.items.map(x => ({
                    ...x,
                    Status:
                        this.selected.findIndex(y => y.id === x.id) >= 0
                            ? {
                                  StatusCode: ResponseStatus.Loading,
                                  Message: 'Saving'
                              }
                            : x.Status
                }));
                of(...this.selected)
                    .pipe(delay(2000))
                    .subscribe(item =>
                        this.Save.next({
                            ...item,
                            original: item,
                            PayRate:
                                values && values.PayRate
                                    ? values.PayRate
                                    : item.PayRate
                        })
                    );
                this.selected = [];
            });

        fromEvent(this.clearButton.nativeElement, 'click')
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                this.syncFormGroup.reset();
            });
    }
    onRowEditInit(item: fromDataServices.PayrateOverride.PayrateOverride) {
        this.clearItemsStatus();
        this.selected = [];
        this.cloned[item.id] = { ...item };
        this.editing = item;
        this.log.verbose({
            type: 'PayrateOverrideComponent:onRowEditInit',
            item,
            cloned: this.cloned,
            editing: this.editing,
            items: this.items
        });
        this.Select.next(item);
    }
    onRowEditSave(item: fromDataServices.PayrateOverride.PayrateOverride) {
        this.log.verbose({
            type: 'PayrateOverrideComponent:onRowEditSave',
            item,
            cloned: this.cloned,
            selected: this.selected,
            items: this.items
        });
        item.Status.StatusCode = ResponseStatus.Loading;
        item.Status.Message = 'Saving';

        this.Save.next(item);
        delete this.cloned[item.id];
        delete this.editing;
        this.selected = [];
    }
    onRowEditCancel(item: fromDataServices.PayrateOverride.PayrateOverride) {
        const index = this.items.findIndex(x => x.id === item.id);
        if (index >= 0) {
            this.items.splice(index, 1, this.cloned[item.id]);
        }
        this.items = [...this.items];

        delete this.cloned[item.id];
        delete this.editing;
    }
    onRowSelect($event) {
        this.log.verbose({
            type: 'PayrateOverrideComponent:onRowSelect',
            $event
        });
        this.Select.next($event.data);
    }
    onRowEditDelete(
        item: fromDataServices.PayrateOverride.PayrateOverride,
        index: number
    ) {
        this.clearItemsStatus();
        this.confirmservice
            .confirmPromise({
                dialogText: `Are you sure you want to delete this override?`
            })
            .then(() => {
                // if (item.id === 'add') {
                //     this.items.splice(index, 1, { ...this.addTemplate } as any);
                // } else {
                this.items.splice(index, 1, this.cloned[item.id]);
                // }

                this.Delete.next([item]);

                delete this.cloned[item.id];
                delete this.editing;
                return;
            })
            .catch(e => {});
    }
    handleOnOpen($event) {
        this.log.verbose({
            type: 'PayrateOverrideComponent:handleOnOpen',
            $event
        });
        const baseHeight = '58vh';
        let accHeight = 0;
        if ($event === 0 || ($event.includes && $event.includes(0))) {
            accHeight += 7.15;
        }
        if ($event === 1 || ($event.includes && $event.includes(1))) {
            accHeight += 12;
        }
        this.dataTable.scrollHeight = `calc(${baseHeight} - ${accHeight}em)`;
        this.log.verbose({
            type: 'PayrateOverrideComponent:handleOnOpen',
            datatableHeight: this.dataTable.scrollHeight
        });
    }
    onDirectReportChange($event) {
        this.log.verbose({
            type: 'PayrateOverrideComponent:onDirectReportChange',
            $event
        });
        this.SelectDirectReport.next($event.value);
    }
    public hasErrorStatus = item =>
        item && item.Status && item.Status.StatusCode >= DisplayMessageMinimum;

    public getErrorsAsArray = (errors: any) => {
        if (!errors) {
            return [];
        }
        const keys = Object.keys(errors);
        this.log.verbose({
            type: 'getErrorsAsArray',
            keys,
            errors,
            values: keys.map(k => ({
                severity: 'error',
                detail: errors[k].toString()
            }))
        });
        return keys.map(k => ({
            severity: 'error',
            detail: errors[k].toString()
        }));
    };
    clearItemsStatus() {
        this.ClearStatus.next();
    }
    onFilterDates(value: string | string[]) {
        this.log.verbose({
            type: 'PayrateOverrideComponent:onFilterDates',
            value
        });
        //this could have multiple selections, if so need to get the start date from the first, and end date from the last
        if (Array.isArray(value)) {
            const date1 = value[0];
            const date2 = value[value.length - 1]; //does this get the last, or beyond the array?
            this.Filter.next({
                StartDate: date1.split('-')[0],
                EndDate: date2.split('-')[1]
            });
        } else {
            const dateParts = value.split('-');
            this.Filter.next({
                StartDate: dateParts[0],
                EndDate: dateParts[1]
            });
        }
    }
    onRecalcRates(value: string[]) {
        this.log.verbose({
            type: 'PayrateOverrideComponent:onRecalcRates',
            value
        });
        const dateParts = value[0].split('-');
        this.RecalcRates.next({
            StartDate: dateParts[0],
            EndDate: dateParts[1]
        });
    }
    canRecalcRates = value => !(Array.isArray(value) && value.length > 1);
}
